// The headless plate clip probe (isEPContentClippedForSize), in a module of its own: why it is in
// neither ep-editor.tsx nor ep-content-utils.ts is in engraved-plates/AGENTS.md ("The editor's
// overflow check also runs without an edit").
import {EP_SHORT_PLATE_SIDE_MARGIN_IN, renderSettings} from '../../rendering/render.def';
import {EP_FRAME_FONT_FAMILY, EP_INHERITED_LINE_HEIGHT, isEpShortPlateOverwrap, transformFontSizePtToCqh} from './ep-content-utils';

/**
 * Headless equivalent of EPEditor's in-editor overflow check (isContentClipped):
 * does the stored plate HTML overflow a plate of the given size?
 * The Quill editor is unmounted during the CAF doc-size step, and has no layout
 * while the plate panel is collapsed, so both use this to surface the same "text
 * is being cut off" warning and block checkout; the personalized-plate module
 * (ep-template-apply) applies it to every resolved template plate, whose width fit
 * stops at a 6 pt floor. It lays the content out in the editor's geometry and
 * applies the editor's rule, so the surfaces never disagree about a plate: the
 * text's ink against the plate's edges, with the plate edge, not the padded box,
 * as the vertical limit (the frame canvas and the print PDF take the same limit,
 * ep-pdf.md step 6).
 */
export const isEPContentClippedForSize = (epContentHtml: string, epWidthInches: number, epHeightInches: number): boolean => {
    if (!epContentHtml || typeof document === 'undefined') return false;

    const probe = document.createElement('div');
    probe.innerHTML = epContentHtml;
    if (!probe.textContent || !probe.textContent.trim()) return false;

    // Exact fractional px — must match the editor/canvas reference geometry so this
    // headless check agrees with them about which strings wrap (flooring the height
    // shrinks the cqh fonts ~3% on a 0.5" plate and flips borderline lines).
    const epW = epWidthInches * renderSettings.referenceDPI;
    const epH = epHeightInches * renderSettings.referenceDPI;
    if (!(epW > 0 && epH > 0)) return false;

    // Padding matches setContainerStyle / EPApprovalPreview: plates at least 1"
    // tall reserve padding on all sides; clamped short plates use the full height
    // but still keep 0.25" side margins.
    const applyPadding = epHeightInches >= 1;
    const padding = applyPadding ? `${renderSettings.topPadding}px ${renderSettings.defaultPadding}px ${renderSettings.defaultPadding}px ${renderSettings.defaultPadding}px` : `0 ${EP_SHORT_PLATE_SIDE_MARGIN_IN * renderSettings.referenceDPI}px`;

    // Stored pt font-sizes are resolved relative to the plate height via the
    // container query, exactly like EPApprovalPreview renders them; HTML that arrives
    // converted already (ep-template-apply passes transformPtToCqh output) is used as it is.
    const epHeightPt = Math.round(epHeightInches * 72);
    const html = /font-size:\s*[\d.]+pt/i.test(epContentHtml) ? transformFontSizePtToCqh(epContentHtml) : epContentHtml;

    // The editor's geometry (setContainerStyle): the plate is the size container and stays
    // unpadded; the content box inside it carries the padding and centres its lines as a flex
    // column, so text taller than the padded box runs into the padding at both ends and passes
    // the plate edge only after that.
    const outer = document.createElement('div');
    outer.style.cssText = `position:absolute;left:-9999px;top:0;width:${epW}px;height:${epH}px;box-sizing:border-box;container-type:size;overflow:visible;visibility:hidden;pointer-events:none;`;
    outer.style.setProperty('--ep-h-pt', String(epHeightPt));

    const inner = document.createElement('div');
    // the frame canvas's family and inherited line-height: a paragraph stored without its own
    // line-height must fit here at the spacing the frame canvas gives it
    inner.style.cssText = `width:100%;height:100%;box-sizing:border-box;padding:${padding};display:flex;flex-direction:column;justify-content:center;font-family:${EP_FRAME_FONT_FAMILY};line-height:${EP_INHERITED_LINE_HEIGHT};white-space:pre-wrap;word-wrap:break-word;text-align:center;overflow:visible;`;
    inner.innerHTML = html;
    inner.querySelectorAll('p, li').forEach(el => ((el as HTMLElement).style.margin = '0'));

    outer.appendChild(inner);
    document.body.appendChild(outer);
    // Same 0.5"-plate multi-line rule as the live editor warning: measured on the
    // probe while it is still attached (client rects need layout).
    const overwrap = isEpShortPlateOverwrap(epContentHtml, inner, epHeightInches);
    // The editor's rule (isContentClipped): the text's ink boxes against the plate's edges, with
    // its tolerance. The editor measures on the 1.5x-scaled container, where 3px is 2px of this
    // reference box.
    const plate = outer.getBoundingClientRect();
    const eps = 2;
    let clipped = false;
    const walker = document.createTreeWalker(inner, NodeFilter.SHOW_TEXT);
    let node: Node | null;
    while (!clipped && (node = walker.nextNode())) {
        if (!(node.textContent || '').trim()) continue;
        const range = document.createRange();
        range.selectNodeContents(node);
        for (const r of Array.from(range.getClientRects())) {
            if (r.width <= 0.5 || r.height <= 0.5) continue;
            if (r.top < plate.top - eps || r.bottom > plate.bottom + eps || r.left < plate.left - eps || r.right > plate.right + eps) {
                clipped = true;
                break;
            }
        }
    }
    document.body.removeChild(outer);
    return overwrap || clipped;
};
